import type { IEventSlot } from '@api/EventSlotsApi';
import type {
	CmsArticleIntroProps,
	CmsCopytextProps,
	CmsDetailHeaderProps,
	CmsEventTicketsProps,
	CmsFaqProps,
	CmsHeadlineProps,
	CmsRelatedEventsProps,
	CmsTextImageTeaserProps,
	CmsImageTeaserProps,
	IArticleIntroCmsBlock,
	ICmsMedia,
	ICopytextCmsBlock,
	IFaqCmsBlock,
	IHeadlineCmsBlock,
	ITextImageCmsBlock,
	IImageTeaserCmsBlock,
	IndexArticleTileProps,
	SlotTicketProps
} from '../types';
import { differenceInMinutes, parseISO } from 'date-fns';
import { matchCmsMediaByImageId } from './cmsComponentMapping';
import { PUBLIC_SHOP_CONTENT_API_BASE_URL } from '$env/static/public';
import type { IEvent } from '@api/EventsApi';
import { formatEventLocation } from './format';
import { calculateEventSlotStatus } from './eventSlotStatus';

export function parseDetailHeader(
	currentEvent: IEvent,
	additionalAggregatedEventData: IEventSlot | undefined,
	usesCmsEventTicketsComponent: boolean
): CmsDetailHeaderProps {
	let durationMinutes = 0;

	if (additionalAggregatedEventData) {
		const startsAt = parseISO(additionalAggregatedEventData.starts_at);
		const endsAt = parseISO(additionalAggregatedEventData.ends_at);
		durationMinutes = differenceInMinutes(endsAt, startsAt);
	}

	return {
		durationMinutes: durationMinutes,
		eventName: currentEvent.name,
		eventType: currentEvent.event_type,
		priceCents: currentEvent.participation_fee_in_cents,
		imageSrc: currentEvent.images.big,
		imageAlt: currentEvent.image_alt,
		metaDescription: currentEvent.meta_description,
		metaTitle: /\|\s*Telekom$/.test(currentEvent.meta_title.trim())
			? currentEvent.meta_title
			: `${currentEvent.meta_title.trim()} | Telekom`,
		usesCmsEventTicketsComponent: usesCmsEventTicketsComponent,
		fsk: currentEvent.fsk
	};
}

export function parseArticleIntro(eventData: IEvent): CmsArticleIntroProps {
	return {
		eventName: eventData.name,
		imageSrc: eventData.images.big,
		metaDescription: eventData.meta_description,
		metaTitle: /\|\s*Telekom$/.test(eventData.meta_title.trim())
			? eventData.meta_title
			: `${eventData.meta_title.trim()} | Telekom`
	};
}

export function parseTextImageTeaser(
	cmsComponentBlock: ITextImageCmsBlock,
	cmsMediaBlocks: ICmsMedia[]
): CmsTextImageTeaserProps {
	const { headline, content, image_position, images } = cmsComponentBlock.columns;

	const imageId = images?.[0];
	const image: ICmsMedia | undefined = matchCmsMediaByImageId(imageId, cmsMediaBlocks);

	let parsedContent;

	try {
		parsedContent =
			typeof content === 'string'
				? JSON.parse(content.replace(/=>/g, ':').replace(/\\"/g, '"').replace(/\\\\/g, '\\')).items
				: content;
	} catch (err) {
		parsedContent = content;
	}

	const props: CmsTextImageTeaserProps = {
		content: parsedContent,
		headline: headline,
		imagePosition: image_position,
		image: {
			src: `${PUBLIC_SHOP_CONTENT_API_BASE_URL}${image?.columns?.image?.url || ''}`,
			alt: image?.columns?.image_alt || ''
		},
		...(cmsComponentBlock.columns.call2action_label
			? { ctaLinkText: cmsComponentBlock.columns.call2action_label }
			: {}),
		...(cmsComponentBlock.columns.call2action_href
			? { ctaLinkUrl: cmsComponentBlock.columns.call2action_href }
			: {})
	};

	return props;
}

export function parseImageTeaser(
	cmsComponentBlock: IImageTeaserCmsBlock,
	cmsMediaBlocks: ICmsMedia[]
): CmsImageTeaserProps {
	const { images } = cmsComponentBlock.columns;
	const imageId = images?.[0];
	const image: ICmsMedia | undefined = matchCmsMediaByImageId(imageId, cmsMediaBlocks);

	const props: CmsImageTeaserProps = {
		image: {
			src: `${PUBLIC_SHOP_CONTENT_API_BASE_URL}${image?.columns?.image?.url || ''}`,
			alt: image?.columns?.image_alt || ''
		}
	};

	return props;
}

export function parseHeadline(cmsComponentBlock: IHeadlineCmsBlock): CmsHeadlineProps {
	const alignmentArray: ('left' | 'center' | 'right')[] = ['left', 'center', 'right'];

	const { headline, position } = cmsComponentBlock.columns;
	return {
		text: headline,
		alignment: alignmentArray[position] || 'left'
	};
}

export function parseCopytext(cmsComponentBlock: ICopytextCmsBlock): CmsCopytextProps {
	let text;
	const content = cmsComponentBlock.columns.content;
	try {
		text =
			typeof content === 'string'
				? JSON.parse(content.replace(/=>/g, ':').replace(/\\"/g, '"').replace(/\\\\/g, '\\')).items
				: content;
	} catch (err) {
		text = content;
	}

	return {
		text
	};
}

export function parseFaq(cmsComponentBlock: IFaqCmsBlock): CmsFaqProps {
	const { headline, items } = cmsComponentBlock.columns;

	return {
		headline: headline,
		items: items || []
	};
}

export function parseRelatedEvents(relatedEvents: IEventSlot[]): CmsRelatedEventsProps {
	return {
		relatedEvents
	};
}

export function parseEventTickets(eventData: IEvent): CmsEventTicketsProps {
	return {
		eventId: eventData.id,
		useExternalRegistrationLink: eventData.external_slots,
		externalRegistrationUrl: eventData.external_slots_url
	};
}

// Index Page has to parse Articles as well (to show a preview tile )
export function parseIndexArticleTile(eventData: IEvent): IndexArticleTileProps {
	if (!eventData.cms_article) {
		throw new Error(`No 'cms_article' found for event ${eventData.name} (/${eventData.slug})`);
	}
	const articleIntroBlock = eventData.cms_article.find(
		(block) => block.resource_class_name === 'ArticleIntro'
	) as IArticleIntroCmsBlock;

	if (!articleIntroBlock) {
		throw new Error(
			`No 'ArticleIntro' CMS component block found for article of event ${eventData.name} (/${eventData.slug})`
		);
	}

	return {
		headline: articleIntroBlock.columns.headline,
		content: articleIntroBlock.columns.content,
		eventSlug: eventData.slug,
		imageSrc: eventData.images.big
	};
}

// Parsing of subcomponents that are not directly related to CMS

export function parseSlotTicket(slot: IEventSlot): SlotTicketProps {
	return {
		slotId: slot.id,
		startsAt: parseISO(slot.starts_at),
		endsAt: parseISO(slot.ends_at),
		location: formatEventLocation(slot.location),
		registrationDeadline: parseISO(slot.registration_deadline),
		status: calculateEventSlotStatus(slot)
	};
}
